// ============================================================================
// DestinationPicker — "where is it actually?", answered by NAME.
//
// WHAT WAS WRONG. The picker this replaces filtered a client-side list of
// facility and office NAMES. Typing "Storage" happened to work; "HQ" did not,
// because "KC Headquarters" does not contain those three letters in that order,
// and neither did "Dock", "MAV", "Silverback" or "Holmes Road". Those are the
// words people actually say. They live in `entity_aliases`, which a browser
// filter cannot see.
//
// WHAT IT IS NOW. Every keystroke past the second asks
// rpc_asset_resolve_destination, which searches ids, names, regions, addresses
// AND aliases, and ranks the result: 1 an id hit, 2 an exact name or alias,
// 3 a prefix, 4 a contains. The function is STABLE and read-only, so it is safe
// on a keystroke — it is debounced anyway, because a scan happens on a phone on
// a hospital corridor's wifi.
//
// ⛔ A PICKER EXCLUDES WHAT THE WRITE WOULD REFUSE (TCSD-0041, TCSD-0129).
// The RPC returns OF-006 "Remote / Overseas" — searching "Remote" finds it —
// and rpc_asset_move then refuses it with TCS_ASSET_REMOTE_NOT_A_PLACE. A
// picker that offers a destination the write rejects is a snapshot presented as
// a guarantee, so it is filtered here. Fixing that inside the function is a
// migration and is proposed in the 2026-08-27 run report.
//
// ⚠️ COVERAGE THIS DOES NOT HAVE. The function's facility half reads
// `active_facilities`, so the 28 Closed / Ended facilities are not findable —
// where the old client-side list searched them on purpose, for the machine left
// behind at a site TCS has exited. Zero machines are recorded at one today.
// Also in the run report.
// ============================================================================
import { useEffect, useRef, useState } from 'react';
import { Building2, Search, Warehouse } from 'lucide-react';
import { supabase } from '../../shared/supabase';
import { rpcErrorMessage } from '../../shared/rpc-error';
import { EXCLUDED_OFFICE_IDS } from './hooks/use-asset-location';

export interface Destination {
  kind: 'office' | 'facility';
  id: string;
  name: string;
  detail: string | null;
  matched_on: string | null;
  rank: number | null;
}

/** Long enough that a phone on corridor wifi is not firing a query per letter,
 *  short enough that the list feels like it is keeping up. */
const DEBOUNCE_MS = 250;
/** One letter matches most of 238 facilities, which is a list nobody reads. */
const MIN_CHARS = 2;

export async function resolveDestinations(query: string, limit = 8): Promise<Destination[]> {
  const { data, error } = await supabase.rpc('rpc_asset_resolve_destination', {
    p_query: query,
    p_limit: limit,
  });
  if (error) throw error;
  return ((data ?? []) as Destination[]).filter((d) => !EXCLUDED_OFFICE_IDS.includes(d.id));
}

export function DestinationPicker({
  value,
  onChange,
  /** Raw text, lifted so the sheet can submit what was typed but never picked. */
  onQueryChange,
  currentId,
  autoFocus = false,
}: {
  value: Destination | null;
  onChange: (d: Destination) => void;
  onQueryChange?: (q: string) => void;
  /** The machine's current location — labelled so nobody "moves" it to where
   *  it already is (the RPC rejects that anyway). */
  currentId?: string | null;
  autoFocus?: boolean;
}) {
  const [q, setQ] = useState('');
  const [hits, setHits] = useState<Destination[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (autoFocus) inputRef.current?.focus();
  }, [autoFocus]);

  useEffect(() => {
    const needle = q.trim();
    if (needle.length < MIN_CHARS) {
      setHits([]);
      setError(null);
      setBusy(false);
      return;
    }
    let cancelled = false;
    setBusy(true);
    const t = setTimeout(() => {
      (async () => {
        try {
          const rows = await resolveDestinations(needle, 8);
          if (!cancelled) {
            setHits(rows);
            setError(null);
          }
        } catch (e) {
          if (!cancelled) {
            setHits([]);
            setError(rpcErrorMessage(e, 'Could not search for a place.'));
          }
        } finally {
          if (!cancelled) setBusy(false);
        }
      })();
    }, DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [q]);

  // Offices first. The function already sorts them ahead at equal rank, but
  // grouping is what a person reads: KC Storage is where machines actually go
  // between sites, so it must not be buried among surgery centres.
  const offices = hits.filter((h) => h.kind === 'office');
  const facilities = hits.filter((h) => h.kind === 'facility');
  const typed = q.trim();

  return (
    <div>
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-tcs-text-muted" />
        <input
          ref={inputRef}
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            onQueryChange?.(e.target.value);
          }}
          placeholder="Storage, HQ, Dock, Lexington, or paste OF-007…"
          className="block w-full rounded-lg border border-tcs-border-input bg-tcs-field py-2.5 pl-9 pr-3 text-base text-tcs-text placeholder:text-tcs-text-muted focus:outline-none"
          aria-label="Search for a facility or office by name"
        />
      </div>

      {value && (
        <p className="mt-2 text-[12.5px] text-tcs-text-secondary">
          Moving to <span className="font-medium text-tcs-text">{value.name}</span>.
        </p>
      )}

      <div className="mt-2 max-h-[19rem] overflow-y-auto rounded-lg border border-tcs-border bg-tcs-raised">
        {typed.length < MIN_CHARS ? (
          <p className="px-3 py-6 text-center text-sm text-tcs-text-muted">
            Type at least {MIN_CHARS} letters. Nicknames work — Storage, Dock, HQ, Denver.
          </p>
        ) : busy ? (
          <p className="px-3 py-6 text-center text-sm text-tcs-text-muted">Searching…</p>
        ) : error ? (
          <p className="px-3 py-6 text-center text-sm text-st-danger-fg-strong">{error}</p>
        ) : hits.length === 0 ? (
          <p className="px-3 py-6 text-center text-sm text-tcs-text-muted">
            No office or facility matches “{typed}”. Try a shorter word.
          </p>
        ) : (
          <>
            {offices.length > 0 && (
              <>
                <Group>Offices &amp; storage</Group>
                {offices.map((o) => (
                  <Row
                    key={o.id}
                    opt={o}
                    selected={value?.id === o.id}
                    isCurrent={currentId === o.id}
                    onPick={onChange}
                  />
                ))}
              </>
            )}
            {facilities.length > 0 && (
              <>
                <Group>Facilities</Group>
                {facilities.map((f) => (
                  <Row
                    key={f.id}
                    opt={f}
                    selected={value?.id === f.id}
                    isCurrent={currentId === f.id}
                    onPick={onChange}
                  />
                ))}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function Group({ children }: { children: React.ReactNode }) {
  return (
    <div className="border-b border-tcs-border bg-tcs-surface px-3 py-1.5 text-[10.5px] font-bold uppercase tracking-wider text-tcs-text-muted">
      {children}
    </div>
  );
}

function Row({
  opt,
  selected,
  isCurrent,
  onPick,
}: {
  opt: Destination;
  selected: boolean;
  isCurrent: boolean;
  onPick: (d: Destination) => void;
}) {
  const Icon = opt.kind === 'office' ? Warehouse : Building2;
  return (
    <button
      type="button"
      // mousedown, not click: a click-outside listener on the sheet would
      // otherwise close it before the click lands.
      onMouseDown={(e) => {
        e.preventDefault();
        onPick(opt);
      }}
      aria-current={selected ? 'true' : undefined}
      className={`flex w-full items-center gap-2.5 border-b border-tcs-border px-3 py-2.5 text-left last:border-b-0 hover:bg-tcs-surface-hover ${
        selected ? 'bg-tcs-accent-bg' : ''
      }`}
    >
      <Icon className="h-4 w-4 shrink-0 text-tcs-text-muted" />
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-medium text-tcs-text">{opt.name}</span>
        {opt.detail && (
          <span className="block truncate text-[11.5px] text-tcs-text-muted">{opt.detail}</span>
        )}
      </span>
      <KindChip kind={opt.kind} />
      {isCurrent && (
        <span className="shrink-0 rounded-full border border-st-info-border bg-st-info-bg px-2 py-0.5 text-[11px] font-medium text-st-info-fg-strong">
          Current
        </span>
      )}
    </button>
  );
}

export function KindChip({ kind }: { kind: 'office' | 'facility' }) {
  return (
    <span className="shrink-0 rounded-full border border-tcs-border bg-tcs-surface px-2 py-0.5 text-[11px] font-medium text-tcs-text-secondary">
      {kind === 'office' ? 'Office' : 'Facility'}
    </span>
  );
}
